/**
 * Selects dépendants Marque ↔ Gamme.
 *
 * S'applique à tout formulaire portant à la fois un `[data-brand-select]` et un
 * `[data-brand-range-select]` — formulaires d'édition, de filtre ou d'export
 * (Éco-score, GS1, …). Aucun enregistrement n'est nécessaire : côté PHP il suffit
 * d'utiliser BrandChoiceType et BrandRangeChoiceType, qui posent ces marqueurs
 * ainsi que le `data-brand-id` de chaque option de gamme.
 *
 *  - Changement de marque → ne montre que les gammes de cette marque
 *  - Retour à « sans marque » → vide la gamme (sinon on enregistre une gamme
 *    orpheline, incohérente avec la marque vide)
 *  - Changement de gamme → sélectionne automatiquement la marque parente
 */
export default function initBrandRangeSelects(root: ParentNode = document): void {
    root.querySelectorAll<HTMLFormElement>("form").forEach(form => {
        const brandSelect = form.querySelector<HTMLSelectElement>("select[data-brand-select]");
        const rangeSelect = form.querySelector<HTMLSelectElement>("select[data-brand-range-select]");
        if (brandSelect && rangeSelect) {
            bind(brandSelect, rangeSelect);
        }
    });
}

function bind(brandSelect: HTMLSelectElement, rangeSelect: HTMLSelectElement): void {
    const allRangeOptions = Array.from(rangeSelect.options).map(o => o.cloneNode(true) as HTMLOptionElement);

    /**
     * Reconstruit la liste des gammes pour la marque donnée.
     * `clearOnEmptyBrand` distingue une action de l'utilisateur (où repasser à
     * « sans marque » doit vider la gamme) de l'initialisation au chargement
     * (où l'on se contente de refléter ce qui est enregistré).
     */
    const filterRanges = (brandId: string, clearOnEmptyBrand: boolean): void => {
        const current = rangeSelect.value;
        rangeSelect.replaceChildren(...allRangeOptions
            .filter(opt => "" === opt.value || "" === brandId || opt.dataset.brandId === brandId)
            .map(opt => opt.cloneNode(true) as HTMLOptionElement));

        const keep = ("" !== brandId || !clearOnEmptyBrand)
            && Array.from(rangeSelect.options).some(opt => opt.value === current);
        rangeSelect.value = keep ? current : "";
    };

    brandSelect.addEventListener("change", () => filterRanges(brandSelect.value, true));

    rangeSelect.addEventListener("change", () => {
        const brandId = rangeSelect.options[rangeSelect.selectedIndex]?.dataset.brandId;
        if (brandId) {
            brandSelect.value = brandId;
            filterRanges(brandId, false);
        }
    });

    filterRanges(brandSelect.value, false);
}
